import { offerResponseDeadline } from './offer-deadline';
import { getLeagueMonth } from './league-monthly-activity';
import { getLeagueOfferWeek, isInOfferWeek, timestampMillis } from './league-offer-week';

function involves(match: Matches.Match, userId: string) {
  return !!userId && !!match.kul_t_id && !!match.kul_r_id &&
    match.kul_t_id !== match.kul_r_id &&
    [match.kul_t_id, match.kul_r_id].includes(userId);
}

/** Only confirmed, decided results belong in a player's win/loss form. */
export function confirmedPlayerMatches(
  matches: readonly Matches.Match[], userId: string, now = Date.now(),
) {
  const seen = new Set<string>();
  return matches.filter(match => {
    const time = timestampMillis(match.mac_saati);
    const scores = [match.kul_t_sonuc, match.kul_r_sonuc];
    if (!involves(match, userId) || match.mac_durum !== 2 ||
      !Number.isFinite(time) || time > now ||
      !scores.every(score => typeof score === 'number' && Number.isInteger(score) && score >= 0) ||
      scores[0] === scores[1] || (match.id && seen.has(match.id))) return false;
    if (match.id) seen.add(match.id);
    return true;
  }).sort((a, b) => timestampMillis(b.mac_saati) - timestampMillis(a.mac_saati));
}

export type PlayActionKind = 'incoming' | 'upcoming' | 'result' | 'approval' | 'sent';

/** A navigation hint only; accepting offers and entering scores keep their existing rules. */
export function getNextPlayAction(
  matches: readonly Matches.Match[], userId: string, leagueId: string, now: number,
) {
  const personal = matches.filter(match => match.id && leagueId &&
    match.lig_temel_id === leagueId && involves(match, userId) &&
    Number.isFinite(timestampMillis(match.mac_saati)));
  // eslint-disable-next-line no-unused-vars -- callback type
  const rules: Array<{ kind: PlayActionKind; accepts: (match: Matches.Match) => boolean }> = [
    { kind: 'incoming', accepts: m => m.mac_durum === 0 && m.kul_r_id === userId && offerResponseDeadline(m) > now },
    { kind: 'upcoming', accepts: m => m.mac_durum === 1 && timestampMillis(m.mac_saati) > now },
    { kind: 'result', accepts: m => m.mac_durum === 1 && timestampMillis(m.mac_saati) <= now },
    { kind: 'approval', accepts: m => m.mac_durum === 4 && timestampMillis(m.mac_saati) <= now },
    { kind: 'sent', accepts: m => m.mac_durum === 0 && m.kul_t_id === userId && offerResponseDeadline(m) > now },
  ];
  const rule = rules.find(item => personal.some(item.accepts));
  if (rule) {
    const { kind, accepts } = rule;
    const candidates = personal.filter(accepts).sort((a, b) =>
      (kind === 'result' || kind === 'approval' ? -1 : 1) *
      (timestampMillis(a.mac_saati) - timestampMillis(b.mac_saati)));
    return { kind, match: candidates[0], count: candidates.length };
  }
  return null;
}

export type OpponentReason = 'available' | 'nearRank' | 'newOpponent';

/** Deterministic suggestions from data already loaded on /oyna. Never grants eligibility. */
export function getOpponentSuggestions(
  players: readonly Users.User[], viewer: Users.User | undefined,
  matches: readonly Matches.Match[], week: ReturnType<typeof getLeagueOfferWeek>, now: number,
) {
  if (!viewer?.verified_account || !viewer.lig_temel_id) return [];
  const personal = matches.filter(m => m.lig_temel_id === viewer.lig_temel_id && involves(m, viewer.id));
  const otherId = (match: Matches.Match) => match.kul_t_id === viewer.id ? match.kul_r_id : match.kul_t_id;
  const blocked = new Set(personal.filter(m => isInOfferWeek(m.mac_saati, week)).map(otherId));
  const month = getLeagueMonth(now);
  const playedThisMonth = new Set(personal.filter(m => m.mac_durum === 2 &&
    timestampMillis(m.mac_saati) >= month.start && timestampMillis(m.mac_saati) <= now).map(otherId));
  const positiveRank = (rank: number) => Number.isInteger(rank) && rank > 0;
  return players.filter(player => player.id !== viewer.id && player.verified_account &&
    player.lig_temel_id === viewer.lig_temel_id && !blocked.has(player.id)).map(player => {
    const distance = positiveRank(viewer.piramit_sira) && positiveRank(player.piramit_sira)
      ? Math.abs(viewer.piramit_sira - player.piramit_sira) : Infinity;
    const nearRank = distance <= 3;
    const fresh = !playedThisMonth.has(player.id);
    const reason: OpponentReason = player.buhaftamusait_mi ? 'available' : nearRank ? 'nearRank' : 'newOpponent';
    return { userId: player.id, reason, score: (player.buhaftamusait_mi ? 4 : 0) + (nearRank ? 2 : 0) + (fresh ? 1 : 0), distance, name: `${player.ad} ${player.soyad}` };
  }).filter(item => item.score > 0).sort((a, b) => b.score - a.score ||
    a.distance - b.distance || a.name.localeCompare(b.name, 'tr') || a.userId.localeCompare(b.userId)).slice(0, 3);
}
